import { deleteAccount, updateAccount } from 'admin/actions/account';
import Select from 'admin/components/atoms/Select';
import { components } from 'admin/schema/docs/api/admin';
import { ColorKey, GradientKey } from 'admin/theme/tokens/colors';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import AccountStatusModal from './AccountStatusModal';
import convertAccountStatusToJapanese from './utils';

type Customer = components['schemas']['Customer'];

type AccountStatus = {
  label: string;
  value: Customer['account_status'];
};

type AccountStatusValue = NonNullable<Customer['account_status']>;

const statusMap: Record<AccountStatusValue, { color: ColorKey; gradient?: GradientKey }> = {
  active: { color: 'green', gradient: '300' },
  suspended: { color: 'white' },
  'pre-closed': { color: 'gray', gradient: '200' },
  closed: { color: 'gray', gradient: '800' },
};

const accountStatusPair = (accountStatus: Customer['account_status']) => ({
  label: convertAccountStatusToJapanese(accountStatus),
  value: accountStatus,
});

const statusList: Customer['account_status'][] = ['active', 'suspended', 'pre-closed', 'closed'];

const getStatusOptions = (currentStatus: Customer['account_status']) => {
  return statusList.map((status) => ({
    ...accountStatusPair(status),
    isDisabled: status === 'closed' && currentStatus !== 'pre-closed',
  }));
};

const AccountStatusForm = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const SelectOptionWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const HelperText = styled.div`
  ${({ theme: { typography, spacing, colors } }) => css`
    font-size: ${typography.fontSize.sm};
    margin-left: ${spacing['16']};
    color: ${colors.gray['300']};
  `}
`;

const CircleStatusIndicator = styled.div<{ $status: AccountStatusValue }>`
  ${({ theme: { colors, borderRadius }, $status }) => {
    const { color, gradient } = statusMap[$status];
    const backgroundColor =
      gradient && color !== 'white' && color !== 'black' ? colors[color][gradient] : colors[color];
    const borderColor = $status === 'suspended' ? colors.gray['200'] : 'transparent';

    return css`
      width: 15px;
      height: 15px;
      border-radius: ${borderRadius.full};
      display: inline-block;
      background-color: ${backgroundColor};
      border: 1px solid ${borderColor};
    `;
  }}
`;

const AccountStatus = ({
  customer,
  setApiMessages,
  onAccountStatusUpdated,
}: {
  customer: Customer;
  setApiMessages: (messages: Array<{ text: string; isError: boolean }>) => void;
  onAccountStatusUpdated?: () => void;
}) => {
  const [accountStatus, setAccountStatus] = useState<AccountStatus>(
    accountStatusPair(customer.account_status)
  );
  const [originalStatus, setOriginalStatus] = useState<AccountStatus>(
    accountStatusPair(customer.account_status)
  );

  const statusOptions = getStatusOptions(originalStatus.value);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const closeModal = () => {
    setIsModalOpen(false);
    setAccountStatus(originalStatus);
  };

  const handleStatusChange = (status: AccountStatus) => {
    setAccountStatus(status);

    if (status.value !== originalStatus.value) {
      setIsModalOpen(true);
    }
  };

  const formatOptionLabel = (option: AccountStatus, { context }: { context: string }) => {
    if (!option.value) return option.label;
    return (
      <>
        <SelectOptionWrapper>
          <CircleStatusIndicator $status={option.value} />
          <div>{option.label}</div>
        </SelectOptionWrapper>
        {option.value === 'closed' && context === 'menu' && (
          <HelperText>※ 退会準備中 → 退会済の順で変更可能です</HelperText>
        )}
      </>
    );
  };

  const onClose = (cashback: boolean) => {
    if (customer.account_uuid) {
      deleteAccount(customer.account_uuid, { cashback })
        .then(() => {
          setOriginalStatus(accountStatus);
          setApiMessages([{ text: '退会しました', isError: false }]);
          setIsModalOpen(false);
          onAccountStatusUpdated?.();
        })
        .catch((err) =>
          Promise.resolve(err).then((res) => {
            const message =
              typeof res === 'object' && res?.message
                ? res.message
                : 'サーバーエラーが発生しました';
            setApiMessages([{ text: `退会に失敗しました (${message})`, isError: true }]);
            closeModal();
          })
        );
    }
  };

  const onConfirm = () => {
    if (customer.account_uuid) {
      updateAccount(customer.account_uuid, { status: accountStatus.value })
        .then(() => {
          setOriginalStatus(accountStatus);
          setApiMessages([{ text: '状態を変更しました', isError: false }]);
          setIsModalOpen(false);
          onAccountStatusUpdated?.();
        })
        .catch((err) =>
          Promise.resolve(err).then((res) => {
            const message =
              typeof res === 'object' && res?.message
                ? res.message
                : 'サーバーエラーが発生しました';
            setApiMessages([{ text: `変更に失敗しました (${message})`, isError: true }]);
            closeModal();
          })
        );
    }
  };

  useEffect(() => {
    const newStatus = accountStatusPair(customer.account_status);
    setAccountStatus(newStatus);
    setOriginalStatus(newStatus);
  }, [customer]);

  return (
    <AccountStatusForm>
      <Select
        fullWidth
        options={statusOptions}
        value={accountStatus}
        onChange={handleStatusChange}
        formatOptionLabel={formatOptionLabel}
      />
      <AccountStatusModal
        customer={customer}
        accountStatus={accountStatus}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    </AccountStatusForm>
  );
};

export default AccountStatus;
